import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import type { ExplainResponse } from '../../services/explainer.service';

export interface ExplanationPanelProps {
  isLoading: boolean;
  explanation: ExplainResponse | null;
  error: string | null;
  onDismiss: () => void;
}

/**
 * Renders the AI explanation result, loading skeleton, or error message.
 * Displayed inside a <Collapse in={isOpen}> in TranscriptEditor.
 */
export function ExplanationPanel({ isLoading, explanation, error, onDismiss }: ExplanationPanelProps) {
  return (
    <Paper variant="outlined" sx={{ mt: 1, p: 2, position: 'relative' }}>
      {/* Dismiss button — top-right corner */}
      <IconButton
        size="small"
        onClick={onDismiss}
        aria-label="Close explanation"
        sx={{ position: 'absolute', top: 4, right: 4 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Loading state: skeleton rows */}
      {isLoading && (
        <Box sx={{ pr: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Analyzing sentence…
            </Typography>
          </Box>
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="80%" sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="85%" />
        </Box>
      )}

      {/* Error state */}
      {!isLoading && error !== null && (
        <Box sx={{ pr: 4 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      )}

      {/* Explanation content: five labeled sections */}
      {!isLoading && error === null && explanation !== null && (
        <Box sx={{ pr: 4 }}>
          <ExplainSection label="Original" content={explanation.original} />
          <ExplainSection label="Translation" content={explanation.translation} />
          <ExplainSection label="Breakdown" content={explanation.breakdown} />
          <ExplainSection label="Nuances" content={explanation.nuances} />
          <ExplainSection label="Overall Feel" content={explanation.feel} />
        </Box>
      )}
    </Paper>
  );
}

interface ExplainSectionProps {
  label: string;
  content: string;
}

function ExplainSection({ label, content }: ExplainSectionProps) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight="bold" component="div">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {content}
      </Typography>
    </Box>
  );
}
